/**
 * PriorImportsPanel - Shows documents and entities from other import paths
 *
 * This component enables cross-import awareness by showing users:
 * 1. Documents that were previously processed in other import paths
 * 2. Vendors/customers that were discovered elsewhere
 * 3. Learned patterns that can be applied
 */

import { useState } from "react";
import {
  FileText,
  Users,
  UserCheck,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  ExternalLink,
  Clock,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

// Type definitions for cross-import data
export interface PriorDocument {
  id: number;
  fileName: string;
  classification: string;
  importPath: "discovery" | "enhanced" | "bulk" | "api";
  lastProcessedAt: number;
  hasExtraction: boolean;
  confidence?: number;
}

export interface KnownVendor {
  id: number;
  name: string;
  normalizedName?: string;
  uid?: string;
  documentCount: number;
  firstSeenAt: number;
}

export interface KnownCustomer {
  id: number;
  name: string;
  normalizedName?: string;
  documentCount: number;
  firstSeenAt: number;
}

export interface LearnedPattern {
  id: number;
  documentType: string;
  usageCount: number;
  lastUsedAt: number;
  confidence: number;
}

export interface PriorImportsPanelProps {
  /** Current import context */
  currentImportPath: "discovery" | "enhanced" | "bulk";
  /** Company ID to fetch prior imports for */
  companyId?: number;
  /** Prior documents from other imports */
  priorDocuments?: PriorDocument[];
  /** Known vendors from other imports */
  knownVendors?: KnownVendor[];
  /** Known customers from other imports */
  knownCustomers?: KnownCustomer[];
  /** Learned patterns that can be applied */
  learnedPatterns?: LearnedPattern[];
  /** Loading state */
  isLoading?: boolean;
  /** Callback when user wants to apply a prior extraction */
  onApplyExtraction?: (documentId: number) => void;
  /** Callback when user wants to link to a known vendor */
  onLinkVendor?: (vendorId: number) => void;
  /** Callback when user wants to refresh cross-import data */
  onRefresh?: () => void;
  className?: string;
}

function formatDate(timestamp: number): string {
  return new Intl.DateTimeFormat("de-CH", {
    dateStyle: "medium",
  }).format(new Date(timestamp));
}

function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffHours = diffMs / (1000 * 60 * 60);
  const diffDays = diffHours / 24;

  if (diffHours < 1) return "just now";
  if (diffHours < 24) return `${Math.round(diffHours)}h ago`;
  if (diffDays < 7) return `${Math.round(diffDays)}d ago`;
  return formatDate(timestamp);
}

function getImportPathLabel(path: string): string {
  switch (path) {
    case "discovery":
      return "Company Discovery";
    case "enhanced":
      return "Bank Import";
    case "bulk":
      return "Bulk Import";
    case "api":
      return "API";
    default:
      return path;
  }
}

/**
 * Import path color variants using design tokens
 * Maps semantic meaning to token colors for theme consistency
 */
function getImportPathColor(path: string): string {
  switch (path) {
    case "discovery":
      return "text-primary bg-primary/10";
    case "enhanced":
      return "text-[var(--success)] bg-[var(--success)]/10";
    case "bulk":
      return "text-[var(--info)] bg-[var(--info)]/10";
    case "api":
      return "text-[var(--warning)] bg-[var(--warning)]/10";
    default:
      return "text-muted-foreground bg-muted";
  }
}

export function PriorImportsPanel({
  currentImportPath,
  companyId,
  priorDocuments = [],
  knownVendors = [],
  knownCustomers = [],
  learnedPatterns = [],
  isLoading = false,
  onApplyExtraction,
  onLinkVendor,
  onRefresh,
  className,
}: PriorImportsPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    documents: priorDocuments.length > 0 && priorDocuments.length <= 5,
    vendors: knownVendors.length > 0 && knownVendors.length <= 10,
    customers: false,
    patterns: false,
  });

  // Filter out documents from current import path
  const relevantDocuments = priorDocuments.filter(
    d => d.importPath !== currentImportPath
  );

  const totalItems =
    relevantDocuments.length +
    knownVendors.length +
    knownCustomers.length +
    learnedPatterns.length;

  // Don't show panel if there's no cross-import data
  if (totalItems === 0 && !isLoading) {
    return null;
  }

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <Card className={cn("overflow-hidden border-primary/20", className)}>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-base">
                    Cross-Import Intelligence
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {isLoading
                      ? "Loading prior imports..."
                      : `${totalItems} items discovered in other imports`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {onRefresh && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={e => {
                      e.stopPropagation();
                      onRefresh();
                    }}
                    disabled={isLoading}
                  >
                    <RefreshCw
                      className={cn("h-4 w-4", isLoading && "animate-spin")}
                    />
                  </Button>
                )}
                {isExpanded ? (
                  <ChevronUp className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 space-y-4">
            {/* Prior Documents Section */}
            {relevantDocuments.length > 0 && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => toggleSection("documents")}
                  className="flex items-center justify-between w-full text-left"
                >
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      Prior Documents ({relevantDocuments.length})
                    </span>
                  </div>
                  {expandedSections.documents ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>

                {expandedSections.documents && (
                  <div className="space-y-2 pl-6">
                    {relevantDocuments.slice(0, 10).map(doc => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-2 rounded-lg bg-muted/30"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex-shrink-0">
                            {doc.hasExtraction ? (
                              <CheckCircle2 className="h-4 w-4 text-[var(--success)]" />
                            ) : (
                              <AlertTriangle className="h-4 w-4 text-[var(--warning)]" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">
                              {doc.fileName}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Badge
                                variant="outline"
                                className={cn(
                                  "text-[10px] px-1.5",
                                  getImportPathColor(doc.importPath)
                                )}
                              >
                                {getImportPathLabel(doc.importPath)}
                              </Badge>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatRelativeTime(doc.lastProcessedAt)}
                              </span>
                            </div>
                          </div>
                        </div>
                        {doc.hasExtraction && onApplyExtraction && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => onApplyExtraction(doc.id)}
                          >
                            Use Extraction
                          </Button>
                        )}
                      </div>
                    ))}
                    {relevantDocuments.length > 10 && (
                      <p className="text-xs text-muted-foreground text-center py-1">
                        + {relevantDocuments.length - 10} more documents
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Known Vendors Section */}
            {knownVendors.length > 0 && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => toggleSection("vendors")}
                  className="flex items-center justify-between w-full text-left"
                >
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      Known Vendors ({knownVendors.length})
                    </span>
                  </div>
                  {expandedSections.vendors ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>

                {expandedSections.vendors && (
                  <div className="space-y-2 pl-6">
                    {knownVendors.slice(0, 15).map(vendor => (
                      <div
                        key={vendor.id}
                        className="flex items-center justify-between p-2 rounded-lg bg-muted/30"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">
                              {vendor.name}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              {vendor.uid && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] px-1.5"
                                >
                                  {vendor.uid}
                                </Badge>
                              )}
                              <span>Seen in {vendor.documentCount} doc(s)</span>
                            </div>
                          </div>
                        </div>
                        {onLinkVendor && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => onLinkVendor(vendor.id)}
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Link
                          </Button>
                        )}
                      </div>
                    ))}
                    {knownVendors.length > 15 && (
                      <p className="text-xs text-muted-foreground text-center py-1">
                        + {knownVendors.length - 15} more vendors
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Known Customers Section */}
            {knownCustomers.length > 0 && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => toggleSection("customers")}
                  className="flex items-center justify-between w-full text-left"
                >
                  <div className="flex items-center gap-2">
                    <UserCheck className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      Known Customers ({knownCustomers.length})
                    </span>
                  </div>
                  {expandedSections.customers ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>

                {expandedSections.customers && (
                  <div className="space-y-2 pl-6">
                    {knownCustomers.slice(0, 15).map(customer => (
                      <div
                        key={customer.id}
                        className="flex items-center justify-between p-2 rounded-lg bg-muted/30"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">
                            {customer.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Seen in {customer.documentCount} doc(s)
                          </div>
                        </div>
                      </div>
                    ))}
                    {knownCustomers.length > 15 && (
                      <p className="text-xs text-muted-foreground text-center py-1">
                        + {knownCustomers.length - 15} more customers
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Learned Patterns Section */}
            {learnedPatterns.length > 0 && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => toggleSection("patterns")}
                  className="flex items-center justify-between w-full text-left"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      Learned Patterns ({learnedPatterns.length})
                    </span>
                  </div>
                  {expandedSections.patterns ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>

                {expandedSections.patterns && (
                  <div className="space-y-2 pl-6">
                    {learnedPatterns.map(pattern => (
                      <div
                        key={pattern.id}
                        className="flex items-center justify-between p-2 rounded-lg bg-muted/30"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">
                            {pattern.documentType}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>Used {pattern.usageCount}x</span>
                            <Badge
                              variant="outline"
                              className="text-[10px] px-1.5"
                            >
                              {pattern.confidence}% confidence
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Summary message */}
            <div className="pt-2 border-t">
              <p className="text-xs text-muted-foreground">
                {relevantDocuments.filter(d => d.hasExtraction).length > 0 && (
                  <>
                    <span className="text-[var(--success)]">
                      {relevantDocuments.filter(d => d.hasExtraction).length}{" "}
                      extractions available
                    </span>
                    {" to reuse. "}
                  </>
                )}
                {knownVendors.length > 0 && (
                  <>
                    <span className="text-primary">
                      {knownVendors.length} vendors
                    </span>
                    {" will be auto-matched. "}
                  </>
                )}
                Cross-import intelligence saves processing time.
              </p>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
